import type { TUseRequestUpdaterProps } from 'features/request/useRequest';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { UerExportActKc3AsExcelQuery } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { exportActKC3AsExcelGql } from 'pages/Project/UerProject/ActKC3List/components/AddOrEditActKc3/addOrEditActKc3Queries';
import { downloadURI } from 'utils/downloadDataURI';

import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

interface IUseActKC3Download {
    actKC3Id: number;
    workOrderId: number;
    includeOtherDetails: boolean;
    closeHandler: () => void;
}

const asyncDownloadURI = (dataUrl: string, name: string) => {
    downloadURI(dataUrl, name);
};

const requestId = 'exportActKC3FromListAsExcel';

export const useActKC3Download = (props: IUseActKC3Download) => {
    const { actKC3Id, includeOtherDetails, workOrderId, closeHandler } = props;

    const { id: projectId } = useParams();
    const { projectData } = useProjectData(projectId, false);
    const dispatch = useDispatch();

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const [resetStoreData] = useResetStoreData({
        requestId,
    });

    const onSuccessExport = useCallback(
        (depsVars: TUseRequestUpdaterProps<UerExportActKc3AsExcelQuery>) => {
            const { dataUrl, name } = depsVars.payload.uerExportActKC3AsExcel?.data ?? {};

            if (dataUrl && name) {
                closeHandler();
                asyncDownloadURI(dataUrl, name);
                resetStoreData(undefined);
            }
        },
        [closeHandler, resetStoreData],
    );

    const [, exportActKC3AsExcelStatus, , exportActKC3AsExcelRequest, , exportActKC3AsExcelAbort] = useRequest({
        requestId,
        query: exportActKC3AsExcelGql,
        withEffect: false,
        dependenciesUpdater: onSuccessExport,
    });

    const onActKc3Download = useCallback(() => {
        dispatch(exportActKC3AsExcelRequest({ actKC3Id, includeOtherDetails }));

        sendGpnAnalyticsEvent({
            componentId: 'rim_unload KS TRI button_pressed',
            projectId,
            contractId: projectData?.contractId,
            KC3Id: actKC3Id,
            orderId: workOrderId,
            methodology: projectData?.methodology,
            projectName: projectData?.name,
            projectType: projectData?.contractType,
            projectDO: projectData?.subsidiary?.name,
        });
    }, [
        dispatch,
        exportActKC3AsExcelRequest,
        actKC3Id,
        includeOtherDetails,
        sendGpnAnalyticsEvent,
        projectId,
        projectData?.contractId,
        projectData?.methodology,
        projectData?.name,
        projectData?.contractType,
        projectData?.subsidiary?.name,
        workOrderId,
    ]);

    const onActKc3AsExcelAbort = useCallback(() => {
        dispatch(exportActKC3AsExcelAbort());
    }, [exportActKC3AsExcelAbort, dispatch]);

    return {
        exportActKC3AsExcelStatus,
        onActKc3AsExcelAbort,
        onActKc3Download,
    };
};
